'use client';

import React from 'react';

export type Status = 'idle' | 'loading' | 'ready' | 'error';
export type ScriptElement = HTMLScriptElement | null;
export type Placement = 'head' | 'body';

export const useScript = (
  src: string,
  appendPlacement: Placement = 'head',
  attributes?: { key: string; value: string }[],
): Status => {
  const [status, setStatus] = React.useState<Status>(src ? 'loading' : 'idle');

  React.useEffect(() => {
    if (!src) {
      setStatus('idle');
      return;
    }

    let script: ScriptElement = document.querySelector(`script[src="${src}"]`);

    if (!script) {
      script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.setAttribute('data-status', 'loading');

      Array.isArray(attributes) &&
        attributes.forEach(attribute => {
          script?.setAttribute(attribute.key, attribute.value);
        });

      if (appendPlacement === 'body') {
        document.body.appendChild(script);
      } else {
        document.head.appendChild(script);
      }

      // Store status in attribute on script
      // This can be read by other instances of this hook
      const setAttributeFromEvent = (event: Event) => {
        script?.setAttribute('data-status', event.type === 'load' ? 'ready' : 'error');
      };

      script.addEventListener('load', setAttributeFromEvent);
      script.addEventListener('error', setAttributeFromEvent);
    } else {
      setStatus(script.getAttribute('data-status') as Status);
    }

    // Script event handler to update status in state
    // Note: Even if the script already exists we still need to add
    // event handlers to update the state for *this* hook instance.
    const setStateFromEvent = (event: Event) => {
      setStatus(event.type === 'load' ? 'ready' : 'error');
    };

    script.addEventListener('load', setStateFromEvent, { once: true });
    script.addEventListener('error', setStateFromEvent, { once: true });

    return () => {
      if (script) {
        script.removeEventListener('load', setStateFromEvent);
        script.removeEventListener('error', setStateFromEvent);
      }
    };
  }, [src, appendPlacement, attributes]);

  return status;
};

export default useScript;
